/**
 * Curve-fit popover (blob `openFitPopover`). A floating panel
 * anchored to a series chip's fit button: pick a fit kind + its one parameter +
 * an optional color override. Color-only edits redraw from cached data
 * (`rerenderChartFromLast`); kind/param edits refetch (`doPlot`) since the fit
 * values change server-side.
 *
 * Mounted imperatively (appended to `document.body`) by `openFitPopover`, which
 * `plot-actions` re-exports as the stable seam the series-picker calls.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { plotState, setSeriesFit, type Fit } from '../../state/plot-state.ts';
import { seriesColor } from './plot-constants.ts';
import { runtime } from './plot-runtime.ts';
import { doPlot, rerenderChartFromLast } from './plot-actions.ts';

interface FitKind {
  value: string;
  display: string;
}

const FIT_KINDS: FitKind[] = [
  { value: '', display: 'None (clear)' },
  { value: 'rolling_mean', display: 'Rolling mean' },
  { value: 'ewma', display: 'EWMA' },
  { value: 'gaussian', display: 'Gaussian' },
  { value: 'linear', display: 'Linear fit' },
  { value: 'poly', display: 'Polynomial' },
  { value: 'exponential', display: 'Exponential' },
];

@customElement('fit-popover')
export class FitPopover extends LightElement {
  @property({ attribute: false }) series = '';
  @property({ attribute: false }) anchorRect: DOMRect | null = null;

  @state() private kind = '';
  @state() private window = 20;
  @state() private alpha = 0.2;
  @state() private sigma = 3;
  @state() private degree = 2;
  @state() private color = '#888888';

  private baseColor = '#888888';

  override connectedCallback(): void {
    super.connectedCallback();
    const existing = plotState.value.fits.find((f) => f.series === this.series);
    this.kind = existing?.kind ?? '';
    const p = existing?.params ?? {};
    if (p.window != null) this.window = p.window;
    if (p.alpha != null) this.alpha = p.alpha;
    if (p.sigma != null) this.sigma = p.sigma;
    if (p.degree != null) this.degree = p.degree;
    this.baseColor = seriesColor(plotState.value, this.series);
    this.color = existing?.color ?? this.baseColor;
    document.addEventListener('mousedown', this.onOutside, true);
    document.addEventListener('keydown', this.onKey, true);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.onOutside, true);
    document.removeEventListener('keydown', this.onKey, true);
  }

  override firstUpdated(): void {
    this.classList.add('fit-popover');
    this.position();
  }

  private position(): void {
    const rect = this.anchorRect;
    if (!rect) return;
    requestAnimationFrame(() => {
      const popRect = this.getBoundingClientRect();
      let top = rect.bottom + window.scrollY + 4;
      let left = rect.left + window.scrollX;
      if (left + popRect.width > window.innerWidth + window.scrollX - 8) {
        left = window.innerWidth + window.scrollX - popRect.width - 8;
      }
      if (top + popRect.height > window.innerHeight + window.scrollY - 8) {
        top = rect.top + window.scrollY - popRect.height - 4;
      }
      this.style.top = `${top}px`;
      this.style.left = `${Math.max(8, left)}px`;
    });
  }

  private onOutside = (e: MouseEvent): void => {
    const target = e.target as HTMLElement;
    if (this.contains(target)) return;
    if (target.closest?.('.chip-fit-btn')) return;
    close();
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };

  private paramsForKind(k: string): Record<string, number> {
    if (k === 'rolling_mean') return { window: Number.isFinite(this.window) && this.window >= 1 ? this.window : 20 };
    if (k === 'ewma') return { alpha: Number.isFinite(this.alpha) ? Math.min(1, Math.max(0.001, this.alpha)) : 0.2 };
    if (k === 'gaussian') return { sigma: Number.isFinite(this.sigma) && this.sigma > 0 ? this.sigma : 3 };
    if (k === 'poly') return { degree: Number.isFinite(this.degree) ? Math.min(5, Math.max(1, this.degree)) : 2 };
    return {};
  }

  private commit(rerenderOnly = false): void {
    const k = this.kind;
    let entry: Fit | null = null;
    if (k) {
      entry = { series: this.series, kind: k, params: this.paramsForKind(k) };
      if (this.color && this.color.toLowerCase() !== this.baseColor.toLowerCase()) {
        entry.color = this.color;
      }
    }
    setSeriesFit(this.series, entry);
    if (rerenderOnly && runtime.lastData) rerenderChartFromLast();
    else if (runtime.lastData) void doPlot();
  }

  private renderParamField(): TemplateResult | typeof nothing {
    if (this.kind === 'rolling_mean') {
      return html`<div class="fit-popover-field">
        <label>Window</label>
        <input type="number" min="2" step="1" .value=${String(this.window)}
          @change=${(e: Event) => { this.window = parseInt((e.target as HTMLInputElement).value, 10); this.commit(); }} />
      </div>`;
    }
    if (this.kind === 'ewma') {
      return html`<div class="fit-popover-field">
        <label>Alpha</label>
        <input type="number" min="0.001" max="1" step="0.01" .value=${String(this.alpha)}
          @change=${(e: Event) => { this.alpha = parseFloat((e.target as HTMLInputElement).value); this.commit(); }} />
      </div>`;
    }
    if (this.kind === 'gaussian') {
      return html`<div class="fit-popover-field">
        <label>Sigma</label>
        <input type="number" min="0.1" step="0.1" .value=${String(this.sigma)}
          @change=${(e: Event) => { this.sigma = parseFloat((e.target as HTMLInputElement).value); this.commit(); }} />
      </div>`;
    }
    if (this.kind === 'poly') {
      return html`<div class="fit-popover-field">
        <label>Degree</label>
        <input type="number" min="1" max="5" step="1" .value=${String(this.degree)}
          @change=${(e: Event) => { this.degree = parseInt((e.target as HTMLInputElement).value, 10); this.commit(); }} />
      </div>`;
    }
    return nothing;
  }

  override render(): TemplateResult {
    return html`
      <div class="fit-popover-title">Fit · ${this.series}</div>
      <div class="fit-popover-field">
        <label>Kind</label>
        <select
          .value=${this.kind}
          @change=${(e: Event) => {
            this.kind = (e.target as HTMLSelectElement).value;
            this.commit();
          }}
        >
          ${FIT_KINDS.map((k) => html`<option value=${k.value} ?selected=${k.value === this.kind}>${k.display}</option>`)}
        </select>
      </div>
      ${this.renderParamField()}
      ${this.kind
        ? html`<div class="fit-popover-field">
            <label>Color</label>
            <span class="fit-color-row">
              <input type="color" .value=${this.color}
                @input=${(e: Event) => { this.color = (e.target as HTMLInputElement).value; this.commit(true); }} />
              <button type="button" class="fit-color-reset" title="Reset to series color"
                @click=${() => { this.color = this.baseColor; this.commit(true); }}>reset</button>
            </span>
          </div>`
        : nothing}
      <div class="fit-popover-actions">
        <button class="btn btn-secondary" @click=${() => close()}>Close</button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fit-popover': FitPopover;
  }
}

let current: FitPopover | null = null;
let currentSeries: string | null = null;

/** Close any open fit popover. */
export function close(): void {
  current?.remove();
  current = null;
  currentSeries = null;
}

/** Open (or toggle) the fit popover for `series`, anchored to `anchor`. */
export function openFitPopover(series: string, anchor: HTMLElement): void {
  if (currentSeries === series) {
    close();
    return;
  }
  close();
  const el = document.createElement('fit-popover');
  el.series = series;
  el.anchorRect = anchor.getBoundingClientRect();
  document.body.appendChild(el);
  current = el;
  currentSeries = series;
}
